import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import ClienteLayout from '../../components/ClienteLayout'
import { useAuth } from '../../lib/AuthContext'
import { supabase } from '../../lib/supabase'
import { getMisPedidos, getAhorroAcumulado, getKitsConProductos, getMisFacturas } from '../../lib/portalClienteApi'

const ESTADO_BADGE = {
  pendiente_pago: { bg: '#fef3c7', color: '#92400e', label: 'Pendiente' },
  programada:     { bg: '#e6fafc', color: '#0f7480', label: 'Confirmada' },
  pagado:         { bg: '#e6fafc', color: '#0f7480', label: 'Confirmada' },
  en_ruta:        { bg: '#dbeafe', color: '#1e40af', label: 'En camino' },
  entregado:      { bg: '#d1fae5', color: '#065f46', label: 'Entregado' },
  cancelado:      { bg: '#fee2e2', color: '#991b1b', label: 'Cancelado' },
}

const TIPO_LABEL = {
  compra_unica:           { titulo: 'Kit · Compra única',  sub: 'Pedido puntual' },
  productos_sueltos:      { titulo: 'Tienda',              sub: 'Productos sueltos' },
  adicional_suscripcion:  { titulo: 'Suscripción',         sub: 'Adicional al kit' },
  suscripcion:            { titulo: 'Suscripción',         sub: 'Mensual' },
}

function fmtFechaCorta(iso) {
  if (!iso) return '—'
  const d = new Date(iso)
  return `${d.getDate()} ${['ene','feb','mar','abr','may','jun','jul','ago','sep','oct','nov','dic'][d.getMonth()]} ${String(d.getFullYear()).slice(-2)}`
}

function fmtMonto(n) {
  return '$' + Number(n || 0).toLocaleString('es-UY', { maximumFractionDigits: 0 })
}

export default function Pedidos() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [pedidos, setPedidos] = useState([])
  const [ahorro, setAhorro] = useState(null)
  const [productosFotos, setProductosFotos] = useState({})  // sku → imagen_url
  const [kitsMap, setKitsMap] = useState({})                // codigo → kit con productos
  const [facturasPorPedido, setFacturasPorPedido] = useState({})  // pedido_id → factura más reciente (no NC)
  const [ncPorPedido, setNcPorPedido] = useState({})              // pedido_id → NC asociada (si existe)
  const [filtro, setFiltro] = useState('todos')  // todos | suscripciones | sueltos
  const [cargando, setCargando] = useState(true)
  const [detalleAbierto, setDetalleAbierto] = useState(null)  // pedido_id del detalle expandido

  useEffect(() => { if (user?.id) cargar() }, [user?.id])

  async function cargar() {
    setCargando(true)
    const [peds, ah, kits, facts] = await Promise.all([
      getMisPedidos(50),
      getAhorroAcumulado(user.id),
      getKitsConProductos(),
      getMisFacturas(user.id, 50),
    ])
    setPedidos(peds)
    setAhorro(ah)

    // Separamos factura "principal" (no-NC) de las NCs, ambas mapeadas por pedido_id
    const fbp = {}
    const ncbp = {}
    for (const f of (facts || [])) {
      if (!f.pedido_id) continue
      const esNC = f.tipo_cfe === 'e_nota_credito' || f.tipo_cfe === 'eTicketNotaCredito' || f.tipo_cfe === 'eFacturaNotaCredito'
      if (esNC) {
        if (!ncbp[f.pedido_id]) ncbp[f.pedido_id] = f
      } else {
        if (!fbp[f.pedido_id]) fbp[f.pedido_id] = f
      }
    }
    setFacturasPorPedido(fbp)
    setNcPorPedido(ncbp)

    // Mapa de kits por código
    const km = {}
    ;(kits || []).forEach(k => { km[k.codigo] = k })
    setKitsMap(km)

    // Recolectar todos los SKUs (de items de pedidos sueltos + de kits que aparecen en pedidos)
    const skus = new Set()
    for (const p of peds) {
      if (Array.isArray(p.items)) p.items.forEach(it => it.sku && skus.add(it.sku))
      if (p.kit_codigo && km[p.kit_codigo]) {
        ;(km[p.kit_codigo].productos || []).forEach(it => it.sku && skus.add(it.sku))
      }
    }
    if (skus.size > 0) {
      const { data } = await supabase
        .from('productos')
        .select('sku, imagen_url')
        .in('sku', [...skus])
      const fmap = {}
      ;(data || []).forEach(p => { if (p.imagen_url) fmap[p.sku] = p.imagen_url })
      setProductosFotos(fmap)
    }
    setCargando(false)
  }

  // Helper: obtener los SKUs (max 4) para mostrar miniatura de un pedido
  function getMiniaturas(p) {
    let skus = []
    if (Array.isArray(p.items) && p.items.length > 0) {
      skus = p.items.map(it => it.sku).filter(Boolean)
    } else if (p.kit_codigo && kitsMap[p.kit_codigo]) {
      skus = (kitsMap[p.kit_codigo].productos || []).map(it => it.sku).filter(Boolean)
    }
    return skus.slice(0, 4)
  }

  const filtrados = useMemo(() => {
    if (filtro === 'todos') return pedidos
    // 2026-06-12: compra_unica = kit pago único, NO es suscripción → va a "Compras sueltas"
    if (filtro === 'suscripciones') return pedidos.filter(p => p.tipo === 'suscripcion')
    if (filtro === 'sueltos') return pedidos.filter(p => p.tipo === 'compra_unica' || p.tipo === 'productos_sueltos' || p.tipo === 'adicional_suscripcion')
    return pedidos
  }, [pedidos, filtro])

  // KPIs
  const totalCount = pedidos.length
  const totalAcumulado = pedidos.filter(p => p.estado === 'entregado' || p.estado === 'pagado' || p.estado === 'programada').reduce((s, p) => s + Number(p.monto_total || 0), 0)
  const pendienteMonto = pedidos.filter(p => p.estado === 'pendiente_pago').reduce((s, p) => s + Number(p.monto_total || 0), 0)
  const pendienteCount = pedidos.filter(p => p.estado === 'pendiente_pago').length
  const ahorroAnio = Number(ahorro?.ahorro_total_anio || ahorro?.ahorro_total || 0)
  const ncList = Object.values(ncPorPedido)
  const ncCount = ncList.length
  const ncMonto = ncList.reduce((s, f) => s + Number(f.monto_total || 0), 0)

  function nProductos(p) {
    if (p.tipo === 'productos_sueltos' || p.tipo === 'adicional_suscripcion') {
      return Array.isArray(p.items) ? p.items.length : 0
    }
    if (p.kit_codigo) return null  // no sabemos sin join
    return null
  }

  return (
    <ClienteLayout title="Mis Pedidos">
      <div className="pe-wrap">
        <div className="pe-headline">
          <h1>Mis Pedidos</h1>
          <p>Histórico completo: suscripciones cobradas y compras sueltas.</p>
        </div>

        {/* KPIs */}
        <div className="pe-kpis">
          <div className="pe-kpi">
            <div className="pe-kpi-label">Total pedidos</div>
            <div className="pe-kpi-num">{totalCount}</div>
            <div className="pe-kpi-sub">desde tu primera compra</div>
          </div>
          <div className="pe-kpi">
            <div className="pe-kpi-label">Acumulado</div>
            <div className="pe-kpi-num">{fmtMonto(totalAcumulado)}</div>
            <div className="pe-kpi-sub">IVA incluido</div>
          </div>
          <div className="pe-kpi">
            <div className="pe-kpi-label">Ahorrado vs mercado</div>
            <div className="pe-kpi-num" style={{ color: 'var(--cl-green)' }}>{fmtMonto(ahorroAnio)}</div>
            <div className="pe-kpi-sub">acumulado total</div>
          </div>
          <div className="pe-kpi">
            <div className="pe-kpi-label">Pendiente de pago</div>
            <div className="pe-kpi-num" style={{ color: pendienteMonto > 0 ? '#f59e0b' : 'var(--cl-muted2)' }}>{fmtMonto(pendienteMonto)}</div>
            <div className="pe-kpi-sub">{pendienteCount} pedido{pendienteCount === 1 ? '' : 's'}</div>
          </div>
          {ncCount > 0 && (
            <div className="pe-kpi">
              <div className="pe-kpi-label">Notas de Crédito</div>
              <div className="pe-kpi-num" style={{ color: '#dc2626' }}>−{fmtMonto(ncMonto)}</div>
              <div className="pe-kpi-sub">{ncCount} NC{ncCount === 1 ? '' : 's'} emitida{ncCount === 1 ? '' : 's'}</div>
            </div>
          )}
        </div>

        {/* Filtros */}
        <div className="pe-tabs">
          <button className={`pe-tab ${filtro === 'todos' ? 'active' : ''}`} onClick={() => setFiltro('todos')}>
            Todos <span className="pe-tab-count">{pedidos.length}</span>
          </button>
          <button className={`pe-tab ${filtro === 'suscripciones' ? 'active' : ''}`} onClick={() => setFiltro('suscripciones')}>
            Suscripciones <span className="pe-tab-count">{pedidos.filter(p => p.tipo === 'suscripcion').length}</span>
          </button>
          <button className={`pe-tab ${filtro === 'sueltos' ? 'active' : ''}`} onClick={() => setFiltro('sueltos')}>
            Compras sueltas <span className="pe-tab-count">{pedidos.filter(p => p.tipo === 'compra_unica' || p.tipo === 'productos_sueltos' || p.tipo === 'adicional_suscripcion').length}</span>
          </button>
        </div>

        {/* Lista de pedidos */}
        {cargando ? (
          <div className="pe-loading">Cargando pedidos…</div>
        ) : filtrados.length === 0 ? (
          <div className="pe-empty">
            <div className="pe-empty-icon">📜</div>
            <h3>{pedidos.length === 0 ? 'Todavía no tenés pedidos' : 'No hay pedidos en este filtro'}</h3>
            <p>{pedidos.length === 0 ? 'Cuando hagas tu primera compra, vas a verla acá con todos los detalles.' : 'Probá con otro filtro.'}</p>
            {pedidos.length === 0 && (
              <Link to="/catalogo" className="pe-empty-cta">Ir a la Tienda →</Link>
            )}
          </div>
        ) : (
          <div className="pe-list">
            {filtrados.map(p => {
              const estadoInfo = ESTADO_BADGE[p.estado] || { bg: '#f1f5f9', color: '#64748b', label: p.estado }
              const tipoInfo = TIPO_LABEL[p.tipo] || { titulo: p.tipo, sub: '' }
              const items = Array.isArray(p.items) ? p.items : []
              return (
                <div className="pe-card" key={p.id}>
                  <div className="pe-card-head">
                    <div className="pe-card-num">
                      <span className="pe-card-numero">{p.numero_orden}</span>
                      <span className="pe-card-tipo">· {tipoInfo.titulo}</span>
                    </div>
                    <span className="pe-card-badge" style={{ background: estadoInfo.bg, color: estadoInfo.color }}>{estadoInfo.label}</span>
                  </div>
                  <div className="pe-card-body">
                    {(() => {
                      const minis = getMiniaturas(p)
                      const totalProds = Array.isArray(p.items) && p.items.length > 0
                        ? p.items.length
                        : (p.kit_codigo && kitsMap[p.kit_codigo]?.productos?.length) || 0
                      const extra = totalProds - minis.length
                      return (
                        <div className="pe-card-imgs">
                          {minis.length === 0 && <div className="pe-card-img-empty">📦</div>}
                          {minis.map((sku, i) => (
                            <div className="pe-card-img" key={sku + i}>
                              {productosFotos[sku] ? <img src={productosFotos[sku]} alt="" /> : <span>📦</span>}
                            </div>
                          ))}
                          {extra > 0 && <div className="pe-card-img-extra">+{extra}</div>}
                        </div>
                      )
                    })()}
                    <div className="pe-card-info">
                      <div className="pe-card-fecha">
                        {p.fecha_entrega_programada ? `Entrega ${fmtFechaCorta(p.fecha_entrega_programada)}` : `Creado ${fmtFechaCorta(p.created_at)}`}
                      </div>
                      <div className="pe-card-meta">
                        {tipoInfo.sub}
                        {items.length > 0 && ` · ${items.length} producto${items.length === 1 ? '' : 's'}`}
                        {p.kit_codigo && kitsMap[p.kit_codigo] && ` · ${kitsMap[p.kit_codigo].nombre}`}
                      </div>
                    </div>
                    <div className="pe-card-monto">{fmtMonto(p.monto_total)}</div>
                  </div>
                  <div className="pe-card-actions">
                    {p.estado === 'pendiente_pago' && (
                      <Link to={`/cliente/pagar-pedido/${p.id}`} className="pe-action pe-action-primary">💳 Pagar</Link>
                    )}
                    {(p.estado === 'entregado' || p.estado === 'pagado' || p.estado === 'programada') && (() => {
                      const f = facturasPorPedido[p.id]
                      if (f?.shortlink_hash) {
                        return (
                          <a className="pe-action"
                             href={`https://limpack.uy/f/${f.shortlink_hash}`}
                             target="_blank" rel="noopener noreferrer"
                             title={`Factura ${f.serie}-${f.numero}`}>
                            🧾 Factura
                          </a>
                        )
                      }
                      return (
                        <button className="pe-action"
                                onClick={() => alert('La factura todavía no se emitió. Te avisamos por WhatsApp y email cuando esté lista.')}
                                style={{ opacity: 0.6 }}>
                          🧾 Factura
                        </button>
                      )
                    })()}
                    {p.estado === 'entregado' && (
                      <button className="pe-action" onClick={() => navigate('/catalogo')}>↻ Repetir</button>
                    )}
                    <button className="pe-action" onClick={() => setDetalleAbierto(detalleAbierto === p.id ? null : p.id)}>
                      👁️ {detalleAbierto === p.id ? 'Cerrar' : 'Detalle'}
                    </button>
                  </div>

                  {detalleAbierto === p.id && (
                    <div className="pe-card-detalle">
                      <div className="pe-detalle-section">
                        <div className="pe-detalle-titulo">📦 Productos ({Array.isArray(p.items) ? p.items.length : 0})</div>
                        {Array.isArray(p.items) && p.items.length > 0 ? (
                          <ul className="pe-detalle-items">
                            {p.items.map((it, i) => (
                              <li key={i}>
                                <span>{it.nombre || it.sku}{it.cantidad > 1 ? ` × ${it.cantidad}` : ''}</span>
                                <span style={{ fontWeight: 700 }}>{fmtMonto((it.precio_unitario || it.precio || 0) * (it.cantidad || 1))}</span>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <div className="pe-detalle-vacio">Sin items detallados</div>
                        )}
                      </div>
                      <div className="pe-detalle-section">
                        <div className="pe-detalle-titulo">📍 Entrega</div>
                        <div className="pe-detalle-info">
                          <div>{p.entrega_direccion || '—'}{p.entrega_apto ? ` apto ${p.entrega_apto}` : ''}</div>
                          <div style={{ color: '#94a3b8', marginTop: 2 }}>
                            {p.entrega_dia_semana || '—'} · {p.entrega_ventana === 'manana' ? 'mañana' : p.entrega_ventana === 'tarde' ? 'tarde' : p.entrega_ventana}
                          </div>
                        </div>
                      </div>
                      <div className="pe-detalle-section">
                        <div className="pe-detalle-titulo">💰 Totales</div>
                        <div className="pe-detalle-info">
                          <div className="pe-detalle-tot-row"><span>Subtotal</span><span>{fmtMonto(p.monto_subtotal)}</span></div>
                          {Number(p.costo_envio) > 0 && (
                            <div className="pe-detalle-tot-row"><span>Envío</span><span>{fmtMonto(p.costo_envio)}</span></div>
                          )}
                          <div className="pe-detalle-tot-row pe-detalle-tot-total"><span>Total</span><span>{fmtMonto(p.monto_total)}</span></div>
                          {ncPorPedido[p.id] && (
                            <>
                              <div className="pe-detalle-tot-row" style={{ color: '#dc2626' }}>
                                <span>↩ NC {ncPorPedido[p.id].serie}-{ncPorPedido[p.id].numero}</span>
                                <span>−{fmtMonto(ncPorPedido[p.id].monto_total)}</span>
                              </div>
                              <div className="pe-detalle-tot-row pe-detalle-tot-total" style={{ color: ncPorPedido[p.id].monto_total >= p.monto_total ? '#94a3b8' : 'var(--cl-text)' }}>
                                <span>Neto cobrado</span>
                                <span>{fmtMonto(Number(p.monto_total) - Number(ncPorPedido[p.id].monto_total))}</span>
                              </div>
                            </>
                          )}
                        </div>
                      </div>

                      {ncPorPedido[p.id] && (
                        <div className="pe-detalle-section" style={{ gridColumn: '1 / -1', borderTop: '1px solid var(--cl-line2)', paddingTop: 12, marginTop: 4 }}>
                          <div className="pe-detalle-titulo" style={{ color: '#dc2626' }}>↩ Nota de Crédito emitida</div>
                          <div className="pe-detalle-info" style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center' }}>
                            <div>
                              <strong>{ncPorPedido[p.id].serie}-{ncPorPedido[p.id].numero}</strong>
                              <span style={{ color: '#94a3b8', marginLeft: 8 }}>
                                {ncPorPedido[p.id].fecha_emision
                                  ? new Date(ncPorPedido[p.id].fecha_emision + 'T12:00:00').toLocaleDateString('es-UY', { day: 'numeric', month: 'short', year: 'numeric' })
                                  : ''}
                              </span>
                            </div>
                            <div style={{ color: '#dc2626', fontWeight: 700 }}>
                              −{fmtMonto(ncPorPedido[p.id].monto_total)}
                            </div>
                            {ncPorPedido[p.id].shortlink_hash && (
                              <a className="pe-action"
                                 href={`https://limpack.uy/f/${ncPorPedido[p.id].shortlink_hash}`}
                                 target="_blank" rel="noopener noreferrer"
                                 style={{ marginLeft: 'auto', fontSize: 12, padding: '6px 12px' }}>
                                📄 Ver PDF
                              </a>
                            )}
                          </div>
                          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 8, lineHeight: 1.5 }}>
                            La factura original quedó anulada con esta NC. Ambos documentos son válidos ante DGI.
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>

      <style>{`
        .pe-wrap { max-width: 1320px; margin: 0 auto; padding: 16px 16px 32px }
        .pe-headline h1 { font-size: 26px; font-weight: 900; color: var(--cl-text); letter-spacing: -.02em }
        .pe-headline p { font-size: 14px; color: var(--cl-muted); margin-top: 4px; line-height: 1.5 }

        .pe-kpis { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-top: 18px }
        .pe-kpi { background: var(--cl-card); border: 1px solid var(--cl-line); padding: 14px; border-radius: 12px }
        .pe-kpi-label { font-size: 10px; color: var(--cl-muted2); font-weight: 800; letter-spacing: .08em; text-transform: uppercase }
        .pe-kpi-num { font-size: 20px; font-weight: 900; color: var(--cl-text); margin-top: 4px; letter-spacing: -.02em }
        .pe-kpi-sub { font-size: 11px; color: var(--cl-muted); margin-top: 2px }

        .pe-tabs {
          display: flex; gap: 8px; margin: 22px 0 14px;
          border-bottom: 1px solid var(--cl-line); padding-bottom: 0;
          overflow-x: auto;
        }
        .pe-tabs::-webkit-scrollbar { display: none }
        .pe-tab {
          flex-shrink: 0;
          background: none; border: 0; padding: 10px 4px; cursor: pointer;
          font-family: inherit; font-size: 13px; font-weight: 700;
          color: var(--cl-muted2); border-bottom: 2px solid transparent;
          margin-bottom: -1px; display: inline-flex; gap: 6px; align-items: center;
        }
        .pe-tab.active { color: var(--cl-text); border-bottom-color: var(--cl-teal) }
        .pe-tab-count { font-size: 11px; opacity: .7; font-weight: 800 }

        .pe-loading { padding: 40px; text-align: center; color: var(--cl-muted) }
        .pe-empty { background: var(--cl-card); border: 1px solid var(--cl-line); border-radius: 16px; padding: 40px 24px; text-align: center; margin-top: 18px }
        .pe-empty-icon { font-size: 48px; margin-bottom: 12px }
        .pe-empty h3 { font-size: 18px; font-weight: 800; color: var(--cl-text) }
        .pe-empty p { font-size: 13px; color: var(--cl-muted); margin: 8px 0 18px; line-height: 1.5 }
        .pe-empty-cta { display: inline-block; background: linear-gradient(135deg, var(--cl-blue), var(--cl-teal)); color: #fff; padding: 12px 22px; border-radius: 999px; font-weight: 800; font-size: 14px; text-decoration: none }

        .pe-list { display: flex; flex-direction: column; gap: 12px }
        .pe-card { background: var(--cl-card); border: 1px solid var(--cl-line); border-radius: 14px; overflow: hidden }

        /* Detalle expandido */
        .pe-card-detalle {
          padding: 14px 16px 16px; border-top: 1px solid var(--cl-line2);
          background: rgba(255,255,255,.02);
          display: grid; grid-template-columns: 1fr; gap: 16px;
        }
        @media (min-width: 640px) {
          .pe-card-detalle { grid-template-columns: 1.4fr 1fr 1fr }
        }
        .pe-detalle-section { font-size: 13px }
        .pe-detalle-titulo { font-size: 11px; font-weight: 800; color: var(--cl-muted2); letter-spacing: .05em; text-transform: uppercase; margin-bottom: 8px }
        .pe-detalle-items { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 6px }
        .pe-detalle-items li { display: flex; justify-content: space-between; gap: 12px; color: var(--cl-text); font-size: 13px; line-height: 1.4 }
        .pe-detalle-vacio { color: var(--cl-muted); font-size: 13px; font-style: italic }
        .pe-detalle-info { color: var(--cl-text); font-size: 13px; line-height: 1.5 }
        .pe-detalle-tot-row { display: flex; justify-content: space-between; gap: 12px; padding: 2px 0 }
        .pe-detalle-tot-total { border-top: 1px solid var(--cl-line2); margin-top: 4px; padding-top: 6px; font-weight: 800; color: var(--cl-text); font-size: 14px }
        .pe-card-head {
          display: flex; align-items: center; justify-content: space-between;
          padding: 12px 16px; border-bottom: 1px solid var(--cl-line2);
          gap: 8px;
        }
        .pe-card-num { display: flex; align-items: baseline; gap: 6px; flex-wrap: wrap; min-width: 0 }
        .pe-card-numero { font-size: 12px; font-weight: 800; color: var(--cl-blue); letter-spacing: .04em; text-transform: uppercase }
        .pe-card-tipo { font-size: 12px; font-weight: 700; color: var(--cl-muted) }
        .pe-card-badge { font-size: 10px; font-weight: 800; padding: 4px 10px; border-radius: 999px; letter-spacing: .04em; text-transform: uppercase; flex-shrink: 0 }

        .pe-card-body { display: flex; align-items: center; justify-content: space-between; gap: 14px; padding: 14px 16px }
        .pe-card-imgs { display: flex; align-items: center; flex-shrink: 0 }
        .pe-card-img {
          width: 38px; height: 46px; border-radius: 6px;
          background: linear-gradient(135deg, #e3d4be, #d4c5b1);
          border: 2px solid #fff; margin-left: -8px;
          box-shadow: 0 2px 6px rgba(0,0,0,.08); overflow: hidden;
          display: flex; align-items: center; justify-content: center; font-size: 16px;
        }
        .pe-card-img:first-child { margin-left: 0 }
        .pe-card-img img { width: 100%; height: 100%; object-fit: cover; display: block }
        .pe-card-img-empty {
          width: 38px; height: 46px; border-radius: 6px;
          background: var(--cl-line2);
          display: flex; align-items: center; justify-content: center; font-size: 18px;
        }
        .pe-card-img-extra {
          margin-left: 6px; font-size: 11px; font-weight: 800;
          color: var(--cl-muted); padding: 6px 8px;
          background: var(--cl-line2); border-radius: 999px;
        }
        .pe-card-info { flex: 1; min-width: 0 }
        .pe-card-fecha { font-size: 14px; font-weight: 800; color: var(--cl-text) }
        .pe-card-meta { font-size: 12px; color: var(--cl-muted); margin-top: 2px }
        .pe-card-monto { font-size: 18px; font-weight: 900; color: var(--cl-text); letter-spacing: -.02em; flex-shrink: 0 }

        .pe-card-actions {
          display: flex; gap: 8px; padding: 10px 14px;
          border-top: 1px solid var(--cl-line2); flex-wrap: wrap;
        }
        .pe-action {
          background: none; border: 0; padding: 6px 10px;
          font-family: inherit; font-size: 12px; font-weight: 700;
          color: var(--cl-blue); cursor: pointer; border-radius: 8px;
          text-decoration: none;
        }
        .pe-action:hover { background: var(--cl-line2) }
        .pe-action-primary {
          background: linear-gradient(135deg, var(--cl-blue), var(--cl-teal));
          color: #fff; padding: 7px 14px;
        }
        .pe-action-primary:hover { background: linear-gradient(135deg, var(--cl-blue), var(--cl-teal)); opacity: .92 }

        @media (min-width: 700px) {
          .pe-kpis { grid-template-columns: repeat(4, 1fr); gap: 14px }
        }
        @media (min-width: 900px) {
          .pe-wrap { padding: 36px 44px }
          .pe-headline h1 { font-size: 32px }
          .pe-kpis { gap: 16px; margin-top: 22px }
          .pe-kpi { padding: 18px }
          .pe-kpi-num { font-size: 24px }
          .pe-kpi-sub { font-size: 12px }
          .pe-tabs { margin: 28px 0 16px; gap: 16px }
          .pe-tab { font-size: 14px }
          .pe-card-head { padding: 14px 20px }
          .pe-card-body { padding: 18px 20px }
          .pe-card-fecha { font-size: 16px }
          .pe-card-monto { font-size: 22px }
          .pe-card-actions { padding: 12px 20px }
          .pe-action { font-size: 13px }
        }
      `}</style>
    </ClienteLayout>
  )
}
